import * as React from "react";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import { AiChatbotCreditsBody } from "./ai-chatbot-credits-panel";
import AgentTokensTab from "./tabs/agent-tokens-tab";
import AnalyticsTab from "./tabs/analytics-tab";
import TokenLimitsTab from "./tabs/token-limits-tab";
import "./ai-chatbot-panel.scss";

// ── AI Chatbot settings shell (KOA-18066) ───────────────────────────────────
// Tabbed page replacing the old single-purpose credits page (same URL —
// Stripe successUrl is built from location.pathname, so the path must not
// change). Owners get Credits | Token Limits | Analytics | Agent Tokens;
// everyone else gets Credits | Agent Tokens — agent tokens are per-user (each
// user manages their own), so that tab is never owner-gated.

export type AiChatbotTabKey = "credits" | "limits" | "analytics" | "agent-tokens";

interface AiChatbotTabOption {
  value: AiChatbotTabKey;
  label: string;
}

const TAB_QUERY_PARAM = "tab";

const OWNER_TAB_OPTIONS: AiChatbotTabOption[] = [
  { value: "credits", label: "Credits" },
  { value: "limits", label: "Token Limits" },
  { value: "analytics", label: "Analytics" },
  { value: "agent-tokens", label: "Agent Tokens" }
];

const MEMBER_TAB_OPTIONS: AiChatbotTabOption[] = [
  { value: "credits", label: "Credits" },
  { value: "agent-tokens", label: "Agent Tokens" }
];

/**
 * The tabs in their on-screen order, so a tab's page slides the way the selection moved. The
 * owner list is the superset — a member's two tabs keep their relative order inside it, so one
 * order serves both.
 */
const TAB_ORDER: string[] = OWNER_TAB_OPTIONS.map((option: AiChatbotTabOption): string => option.value);

const OWNER_ONLY_TABS: readonly AiChatbotTabKey[] = ["limits", "analytics"];

/** A deep link to an owner-only tab must not render it for a member. */
export function resolveVisibleTab(requested: AiChatbotTabKey, isDomainOwner: boolean): AiChatbotTabKey {
  return !isDomainOwner && OWNER_ONLY_TABS.includes(requested) ? "credits" : requested;
}

export function parseTabKey(raw: string | null): AiChatbotTabKey {
  const known: AiChatbotTabOption | undefined = OWNER_TAB_OPTIONS.find(
    (option: AiChatbotTabOption): boolean => option.value === raw
  );
  return known ? known.value : "credits";
}

/**
 * Resolve the initial tab from the URL. A Stripe return (`session_id` in the
 * query) always forces Credits — the verify flow lives inside the credits
 * component and must mount immediately.
 */
function resolveInitialTab(): AiChatbotTabKey {
  if (typeof window === "undefined") return "credits";
  const params: URLSearchParams = new URLSearchParams(window.location.search);
  if (params.has("session_id")) return "credits";
  return parseTabKey(params.get(TAB_QUERY_PARAM));
}

interface State {
  activeTab: AiChatbotTabKey;
}

class AiChatbotPanel extends React.PureComponent<IWithHistoryProp & IWithContextProp, State> {
  state: State = { activeTab: resolveInitialTab() };

  private handleTabChange = (value: string) => {
    const tab: AiChatbotTabKey = parseTabKey(value);
    this.setState({ activeTab: tab });
    // Keep the tab on the URL for refresh/deep-link, without a router
    // navigation (mirrors the session_id scrub in the credits panel).
    const url: URL = new URL(window.location.href);
    if (tab === "credits") url.searchParams.delete(TAB_QUERY_PARAM);
    else url.searchParams.set(TAB_QUERY_PARAM, tab);
    window.history.replaceState({}, "", url.toString());
  };

  /** One tab's page, by tab key — rendered per pane, so the page that leaves keeps its own state. */
  private renderTab = (tab: string): React.ReactNode => {
    switch (tab) {
      case "agent-tokens":
        return <AgentTokensTab />;
      case "limits":
        return <TokenLimitsTab />;
      case "analytics":
        return <AnalyticsTab />;
      default:
        return <AiChatbotCreditsBody showHeader={false} />;
    }
  };

  render() {
    const isDomainOwner: boolean = !!this.props.context?.userInfo?.isDomainOwner;
    const activeTab: AiChatbotTabKey = resolveVisibleTab(this.state.activeTab, isDomainOwner);

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="sparkles" type="datasource-icons" />
            <KuikaH4 data-testid="aiChatbotCreditsPageTitle">AI Chatbot</KuikaH4>
          </div>
        </div>

        <div className="aichat-panel__tabs">
          <KuikaTabs
            size="md"
            options={isDomainOwner ? OWNER_TAB_OPTIONS : MEMBER_TAB_OPTIONS}
            value={activeTab}
            onChange={this.handleTabChange}
            dataTestId="aiChatbotTabs"
          />
        </div>

        <div className="aichat-panel__body">
          {/* The RESOLVED tab, never the raw state: a member deep-linking to an owner-only tab
              is shown Credits, and the pane must follow that rather than the request. */}
          <KuikaTabContent activeKey={activeTab} order={TAB_ORDER} render={this.renderTab} />
        </div>
      </div>
    );
  }
}

export default withContext<{}>(withManagement<{}>(withHistory<{}>(AiChatbotPanel)));
